/**
 * MODULE: assistantEmailDraftUtils.ts
 * PURPOSE: Detect and parse follow-up / outreach email drafts in assistant markdown replies.
 * RECENT: 2026-06-06 [2.2.1330] — Parse Subject/body for **`AssistantEmailDraftCard`**; **`EMAIL_TEMPLATES_MODULE_URL`** for library link.
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 95 / 150 lines
 */

export const EMAIL_TEMPLATES_MODULE_URL = '/sales-hub/library?type=email';

export interface ParsedAssistantEmailDraft {
  intro: string;
  subject: string;
  body: string;
  outro: string;
  fullText: string;
}

const SUBJECT_LINE_RE = /^\s*\*?\*?Subject\*?\*?\s*:\s*(.+?)\s*$/im;

const OUTRO_START_RE =
  /\n(?:---+\n|(?:\*{0,2}Feel free to customize|(?:\*{0,2})?Let me know if|(?:\*{0,2})?I hope this|(?:\*{0,2})?You can (?:also )?customize))/i;

/** True when assistant text looks like a drafted email (Subject line + email cues). */
export function looksLikeAssistantEmailDraft(text: string): boolean {
  const trimmed = text?.trim() ?? '';
  if (!trimmed || !SUBJECT_LINE_RE.test(trimmed)) return false;
  const lower = trimmed.toLowerCase();
  const hasEmailCue = /\b(email|follow[- ]?up|outreach|e-mail)\b/i.test(lower);
  const hasDraftCue = /\b(draft|here'?s|wrote|composed)\b/i.test(lower);
  const hasEmailBodyCue = /\b(dear|hi |hello |best regards|kind regards|sincerely|thank you for)\b/i.test(
    lower,
  );
  return hasDraftCue || (hasEmailCue && hasEmailBodyCue) || (hasEmailBodyCue && /\n\n/.test(trimmed));
}

function stripOuterFence(text: string): string {
  const m = text.trim().match(/^```(?:\w+)?\s*\n([\s\S]*?)\n```$/);
  return m ? m[1].trim() : text.trim();
}

/** Parse subject/body from a markdown assistant email draft. */
export function parseAssistantEmailDraft(text: string): ParsedAssistantEmailDraft | null {
  if (!looksLikeAssistantEmailDraft(text)) return null;

  const content = stripOuterFence(text);
  const subjectMatch = content.match(SUBJECT_LINE_RE);
  if (!subjectMatch || subjectMatch.index === undefined) return null;

  const subject = subjectMatch[1].replace(/\*+/g, '').trim();
  if (!subject) return null;

  const subjectLineEnd = subjectMatch.index + subjectMatch[0].length;
  const intro = content.slice(0, subjectMatch.index).trim();
  const afterSubject = content.slice(subjectLineEnd);

  const outroMatch = afterSubject.match(OUTRO_START_RE);
  const bodyEnd = outroMatch?.index !== undefined && outroMatch.index > 16 ? outroMatch.index : afterSubject.length;
  const body = afterSubject.slice(0, bodyEnd).trim();
  const outro = afterSubject.slice(bodyEnd).trim();

  if (!body || body.length < 16) return null;

  const fullText = [
    intro,
    intro ? '' : null,
    `Subject: ${subject}`,
    '',
    body,
    outro ? '' : null,
    outro,
  ]
    .filter((line) => line !== null)
    .join('\n');

  return { intro, subject, body, outro, fullText };
}

export function formatAssistantEmailDraftCopy(draft: {
  intro?: string;
  subject: string;
  body: string;
  outro?: string;
}): string {
  return [
    draft.intro?.trim(),
    draft.intro?.trim() ? '' : null,
    `Subject: ${draft.subject}`,
    '',
    draft.body,
    draft.outro?.trim() ? '' : null,
    draft.outro?.trim(),
  ]
    .filter((line) => line !== null && line !== undefined)
    .join('\n');
}

export function buildEmailDraftImprovePrompt(draft: { subject: string; body: string }): string {
  return [
    'Please improve this email draft — make it clearer, more professional, and concise while keeping the same intent and placeholders:',
    '',
    `Subject: ${draft.subject}`,
    '',
    draft.body,
  ].join('\n');
}
